import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Paper,
  Divider,
  Card,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { baseurl } from "domain";
import Loading from "components/Loading";
import { convertTo12Hour } from "utils/converterUtils";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

function BookingForm() {
  const navigate = useNavigate();

  // Separate state variables
  const [date, setDate] = useState("");
  const [type, setType] = useState("");
  const [duration, setDuration] = useState("");
  const [slot, setSlot] = useState("");
  const [start_time, setStartTime] = useState("");
  const [end_time, setEndTime] = useState("");
  const [name, setName] = useState("");
  const [insName, setInsName] = useState("NA");
  const [email, setEmail] = useState("puwj2013@gmail.com");
  const [phnNo, setPhnNo] = useState("");
  const [amount, setAmount] = useState("");
  const [gstNo, setGSTNo] = useState("NA");
  const [govId, setGovId] = useState("NA");
  const [subject, setSubject] = useState("");
  const [address, setAddress] = useState("Pune");
  const [pinCode, setPinCode] = useState("411030");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false); // For full screen loading
  const [slotsLoading, setSlotsLoading] = useState(false); // For slots loading

  // Get the current step
  const getActiveStep = () => {
    if (!date) return 0;
    if (!type) return 1;
    if (!duration) return 2;
    if (!slot) return 3;
    return 4;
  };

  // Fetch slots when date, type, or duration changes
  useEffect(() => {
    if (date && type && duration) {
      fetchSlots();
    }
  }, [date, type, duration]);

  const fetchSlots = async () => {
    try {
      setSlotsLoading(true); // Start loading slots
      const response = await axios.get(
        `https://${baseurl}/available_slots_conf?date=${date}&duration=${duration}&type=${type}`
      );
      setAvailableSlots(response.data.available_slots);
      setSlotsLoading(false); // Stop loading slots when received
    } catch (error) {
      console.error("Error fetching slots:", error);
      alert("Failed to fetch slots. Please try again.");
      setSlotsLoading(false); // Stop loading slots on error too
    }
  };

  const handleSubmit = () => {
    setOpenConfirm(true);
  };

  const a_name = localStorage.getItem("name");

  const confirmBooking = async () => {
    try {
      setLoading(true); // Show full screen loading indicator
      setOpenConfirm(false); // Close the dialog
      
      await axios.post(`https://${baseurl}/book_slot_conf`, {
        date,
        type,
        duration,
        slot,
        name,
        insName,
        email,
        phnNo,
        amount,
        start_time,
        end_time,
        gstNo,
        govId,
        subject,
        a_name,
        address,
        pinCode,
        remark
      });
      
      setLoading(false); // Hide loading indicator
      alert("Slot booked successfully!");
      navigate("/managePressConfHall");
    } catch (error) {
      setLoading(false); // Hide loading indicator
      console.error("Error booking slot:", error);
      alert("Failed to book slot. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Book Press Conference Hall
                </MDTypography>
              </MDBox>
              
              <MDBox pt={3} pb={3} px={3}>
                {/* Date Selection */}
                <MDBox mb={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Select Date
                  </MDTypography>
                  <MDInput
                    type="date"
                    fullWidth={false}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    sx={{ width: "220px" }}
                  />
                </MDBox>
                
                {date && (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Select Type
                    </MDTypography>
                    <MDBox display="flex" gap={2}>
                      <MDButton
                        variant={type === "press-conf" ? "gradient" : "outlined"}
                        color={type === "press-conf" ? "info" : "secondary"}
                        onClick={() => setType("press-conf")}
                      >
                        Press Conference
                      </MDButton>
                      <MDButton
                        variant={type === "program" ? "gradient" : "outlined"}
                        color={type === "program" ? "info" : "secondary"}
                        onClick={() => setType("program")}
                      >
                        Program
                      </MDButton>
                    </MDBox>
                  </MDBox>
                )}
                
                {type && (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Select Duration
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {(type === "press-conf"
                        ? [30, 60, 120]
                        : [120, 180, 240, 300, 360, 420, 480, 540, 600]
                      ).map((min) => (
                        <MDButton
                          key={min}
                          variant={duration === String(min) ? "gradient" : "outlined"}
                          color={duration === String(min) ? "info" : "secondary"}
                          onClick={() => {
                            setDuration(String(min));
                            setSlot(""); // Reset slot when changing duration
                          }}
                          sx={{ 
                            minWidth: "60px",
                            margin: "0.25rem"
                          }}
                        >
                          {min < 60 ? `${min} mins` : `${(min / 60).toFixed(0)} hours`}
                        </MDButton>
                      ))}
                    </MDBox>
                  </MDBox>
                )}
                
                {duration && (
                  <MDBox mb={3}>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <MDTypography variant="h6" fontWeight="medium">
                        Select Slot
                      </MDTypography>
                      {slotsLoading && (
                        <CircularProgress size={20} color="info" sx={{ ml: 2 }} />
                      )}
                    </MDBox>
                    
                    {!slotsLoading && availableSlots.length > 0 ? (
                      <MDBox display="flex" flexWrap="wrap" gap={1}>
                        {availableSlots.map((s, index) => (
                          <MDButton
                            key={index}
                            variant={slot === `${s.start}-${s.end}` ? "gradient" : "outlined"}
                            color={slot === `${s.start}-${s.end}` ? "info" : "secondary"}
                            onClick={() => { 
                              setSlot(`${s.start}-${s.end}`); 
                              setStartTime(s.start); 
                              setEndTime(s.end); 
                            }}
                            sx={{ 
                              minWidth: "120px",
                              margin: "0.25rem"
                            }}
                          >
                            {convertTo12Hour(s.start)} - {convertTo12Hour(s.end)}
                          </MDButton>
                        ))}
                      </MDBox>
                    ) : !slotsLoading ? (
                      <MDBox p={2} bgColor="light" borderRadius="lg">
                        <MDTypography color="error">
                          No slots available for this date and duration.
                        </MDTypography>
                      </MDBox>
                    ) : null}
                  </MDBox>
                )}
                
                {slot && (
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Contact Details
                    </MDTypography>
                    <Divider />
                    
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Name"
                          fullWidth
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <MDInput
                          label="Subject"
                          fullWidth
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Address"
                          fullWidth
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Pincode"
                          fullWidth
                          value={pinCode}
                          onChange={(e) => setPinCode(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Organization Name"
                          fullWidth
                          value={insName}
                          onChange={(e) => setInsName(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Email"
                          fullWidth
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Phone"
                          fullWidth
                          value={phnNo}
                          onChange={(e) => setPhnNo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="Amount"
                          fullWidth
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="GST No."
                          fullWidth
                          value={gstNo}
                          onChange={(e) => setGSTNo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="GOV ID"
                          fullWidth
                          value={govId}
                          onChange={(e) => setGovId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <MDBox display="flex" justifyContent="center">
                          <MDButton variant="gradient" color="info" onClick={handleSubmit}>
                            Book Slot
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Confirmation Modal */}
      <Dialog 
        open={openConfirm} 
        onClose={() => setOpenConfirm(false)}
      >
        <DialogTitle>
          Confirm Booking
        </DialogTitle>
        <DialogContent>
          <MDBox my={2}>
            <MDTypography variant="body2">Are you sure you want to book this slot?</MDTypography>
          </MDBox>
          
          <MDBox p={2} bgColor="light" borderRadius="lg" my={2}>
            <MDTypography variant="body2" mb={1}><strong>Date:</strong> {date}</MDTypography>
            <MDTypography variant="body2" mb={1}><strong>Type:</strong> {type === "press-conf" ? "Press Conference" : "Program"}</MDTypography>
            <MDTypography variant="body2" mb={1}><strong>Time:</strong> {start_time && end_time ? `${convertTo12Hour(start_time)} - ${convertTo12Hour(end_time)}` : slot}</MDTypography>
            <MDTypography variant="body2"><strong>Duration:</strong> {duration && `${parseInt(duration) / 60} hours`}</MDTypography>
          </MDBox>
          
          <MDInput
            label="Remark"
            fullWidth
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton 
            color="secondary" 
            onClick={() => setOpenConfirm(false)}
          >
            Cancel
          </MDButton>
          <MDButton 
            variant="gradient" 
            color="info" 
            onClick={confirmBooking}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default BookingForm;
